// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */ from 'react';

import { LoadableImg } from '@zapier/design-system-microlibrary-shared';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { Button, Link, Spacer, Text } from '@zapier/design-system';
import { FlexLayout, Modal } from '@zapier/common-components';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import { type Size } from '@zapier/design-system-company/src/types';

/** @jsx isolateComponent */

type Props = {
  /** Callback to be executed when the decline button is clicked */
  onClose: (e: SyntheticMouseEvent<*>) => void,
  /** Current user's id (for tracking) */
  profileId: string,
  /** Size */
  size: Size,
  /** The relative path to the upgrade page */
  upgradeUrl: string,
};

const Styles = labelStyles(__filename, {
  root: {
    textAlign: 'center',
  },

  buttons: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
});

/**
 * Shown to the user when their companies trial has ended.
 */
const TrialEndedModal = (props: Props) => {
  const { trackEvent } = useTrackingContext();

  const handleLinkSupport = (e: SyntheticMouseEvent<*>) => {
    trackEvent('company-trial-ended-support-extend-trial-clicked', {
      profileId: props.profileId,
    });
    props.onClose(e);
  };

  const handleNoThanks = (e: SyntheticMouseEvent<*>) => {
    trackEvent('company-trial-ended-no-thanks-clicked', {
      profileId: props.profileId,
    });
    props.onClose(e);
  };

  return (
    <Modal overflowY="auto">
      <div css={Styles.root}>
        <LoadableImg
          alt="Zapling in the dark with a flashlight"
          aspectRatio="25:9"
          hasRadius={false}
          src="https://zapier-images.imgix.net/storage/photos/1e6f788cc2fd594c22a2647c42627033.png"
        />

        <Spacer height={25} />

        <Text color="grey900" tag="h2" type="subtitle900">
          Your Zapier for Companies 14-day trial has ended
        </Text>

        <Spacer height={25} />

        <Text color="grey900" tag="p" type="bodyText900">
          Ready to make the switch? Upgrade now to continue using our advanced
          administrative and security controls.
        </Text>

        <Spacer height={20} />

        <FlexLayout
          direction={props.size === 'small' ? 'column' : 'row'}
          justify="center"
        >
          <Button color="greyAlt" isFullWidth={true} onClick={handleNoThanks}>
            No Thanks
          </Button>

          <Button
            hasShadow={true}
            href={props.upgradeUrl}
            isFullWidth={true}
            onClick={props.onClose}
          >
            Upgrade Plan
          </Button>
        </FlexLayout>

        <Spacer height={20} />

        <Text fontWeight={600} tag="p" type="altText900">
          Need more time?
        </Text>

        <Spacer height={5} />

        <Text tag="p" type="altText900">
          <Link href="/app/contact-us" onClick={handleLinkSupport}>
            Contact support to extend your trial
          </Link>
        </Text>
      </div>
    </Modal>
  );
};

export default TrialEndedModal;
